import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import Card from "../ui/Card";
import DateRangePicker from "../ui/DateRangePicker";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  formatMissingDatesPreview,
  formatDateRangeLabel,
  formatFlexHours,
  getFlexHoursClass,
  getTimeReportingStatusLabel,
  getTimeReportingStatusClass,
} from "../../utils/timeReportingCompletion";

dayjs.extend(isoWeek);

const QUICK_FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
];

function getQuickFilterRange(key) {
  const today = dayjs();
  switch (key) {
    case "today":
      return { from: today.startOf("day").toDate(), to: today.endOf("day").toDate() };
    case "yesterday": {
      const y = today.subtract(1, "day");
      return { from: y.startOf("day").toDate(), to: y.endOf("day").toDate() };
    }
    case "week":
      return {
        from: today.startOf("isoWeek").toDate(),
        to: today.endOf("isoWeek").toDate(),
      };
    case "month":
    default:
      return {
        from: today.startOf("month").toDate(),
        to: today.endOf("month").toDate(),
      };
  }
}

export default function TimeReportingDashboard({
  scope,
  scopeId,
  showOrgColumn = true,
}) {
  const [dateRange, setDateRange] = useState({
    from: dayjs().startOf("month").toDate(),
    to: dayjs().endOf("month").toDate(),
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgFilter, setOrgFilter] = useState(null);
  const [openFlagCount, setOpenFlagCount] = useState(0);

  const endpoint =
    scope === "customer"
      ? `/admin/customers/${scopeId}/time-reporting-overview`
      : `/admin/organisations/${scopeId}/time-reporting-overview`;

  useEffect(() => {
    if (!scopeId || !dateRange?.from || !dateRange?.to) return;
    setLoading(true);
    setError(null);
    const from = dayjs(dateRange.from).format("YYYY-MM-DD");
    const to = dayjs(dateRange.to).format("YYYY-MM-DD");
    axiosInstance
      .get(endpoint, { params: { from, to } })
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error(err);
        setError("Failed to load time reporting overview");
      })
      .finally(() => setLoading(false));
  }, [scopeId, dateRange, endpoint]);

  useEffect(() => {
    axiosInstance
      .get("/admin/report-flags/summary")
      .then((res) => setOpenFlagCount(res.data?.openTotal || 0))
      .catch(() => setOpenFlagCount(0));
  }, [scopeId]);

  const summary = data?.summary || {};
  const organisations = data?.organisations || [];
  const allUsers = data?.users || [];

  const filteredUsers = useMemo(() => {
    if (!orgFilter) return allUsers;
    return allUsers.filter((u) => String(u.organisationId) === String(orgFilter));
  }, [allUsers, orgFilter]);

  const attentionUsers = useMemo(
    () => allUsers.filter((u) => u.status !== "complete").slice(0, 5),
    [allUsers]
  );

  const handleQuickFilter = (key) => {
    setActiveQuickFilter(key);
    setDateRange(getQuickFilterRange(key));
  };

  const handleDateRangeChange = (range) => {
    setActiveQuickFilter(null);
    setDateRange(range);
  };

  const reportMonth = dayjs(dateRange.from).format("YYYY-MM");
  const rangeLabel = formatDateRangeLabel(dateRange.from, dateRange.to);

  return (
    <Card className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Time reporting overview</h3>
          {openFlagCount > 0 && (
            <Link
              to={`/admin/follow-up${scope === "organisation" ? `?organisationId=${scopeId}` : ""}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-xs font-medium hover:bg-amber-100"
            >
              {openFlagCount} open flag{openFlagCount === 1 ? "" : "s"}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => handleQuickFilter(f.key)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  activeQuickFilter === f.key
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder="Select range"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-600">Loading time reporting data…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <StatChip
              label="Fully registered"
              value={summary.fullyRegisteredCount ?? 0}
              highlight={summary.withGapsCount === 0}
            />
            <StatChip
              label="With gaps"
              value={summary.withGapsCount ?? 0}
              warn={summary.withGapsCount > 0}
            />
            <StatChip
              label="Missing workdays"
              value={summary.totalMissingWorkdays ?? 0}
              warn={summary.totalMissingWorkdays > 0}
            />
            <StatChip
              label="Hours logged"
              value={summary.totalHours?.toFixed?.(1) ?? summary.totalHours ?? "0"}
            />
          </div>

          {summary.withGapsCount > 0 && (
            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              <span className="font-medium">
                {summary.withGapsCount} {summary.withGapsCount === 1 ? "person has" : "people have"} missing days
              </span>
              {" "}in {rangeLabel}
              {attentionUsers.length > 0 && (
                <>
                  {" · "}
                  {attentionUsers.map((u, i) => (
                    <span key={u._id}>
                      {i > 0 && ", "}
                      <Link
                        to={buildReportLink(u._id, reportMonth)}
                        className="underline hover:text-amber-950"
                      >
                        {u.firstname} ({u.missingWorkdays})
                      </Link>
                    </span>
                  ))}
                  {summary.withGapsCount > attentionUsers.length && " …"}
                </>
              )}
            </div>
          )}

          {showOrgColumn && organisations.length > 1 && (
            <div className="overflow-x-auto mb-4">
              <table className="table-auto w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Organisation</th>
                    <th className="text-right p-2">Members</th>
                    <th className="text-right p-2">Fully registered</th>
                    <th className="text-right p-2">With gaps</th>
                    <th className="text-right p-2">Missing workdays</th>
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {organisations.map((org) => (
                    <tr key={org._id} className="border-t">
                      <td className="p-2">{org.name}</td>
                      <td className="p-2 text-right">{org.memberCount ?? 0}</td>
                      <td className="p-2 text-right">{org.fullyRegisteredCount ?? 0}</td>
                      <td className="p-2 text-right">{org.withGapsCount ?? 0}</td>
                      <td className="p-2 text-right">{org.totalMissingWorkdays ?? 0}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() =>
                            setOrgFilter(
                              orgFilter === String(org._id) ? null : String(org._id)
                            )
                          }
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {orgFilter === String(org._id) ? "Show all" : "View details"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {orgFilter
                  ? `Team · ${organisations.find((o) => String(o._id) === orgFilter)?.name || ""}`
                  : "All members"}
              </h4>
              <table className="table-auto w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    {showOrgColumn && organisations.length > 1 && (
                      <th className="text-left p-2">Organisation</th>
                    )}
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Completion</th>
                    <th className="text-left p-2">Missing days</th>
                    <th className="text-right p-2">Hours</th>
                    <th
                      className="text-right p-2"
                      title="Hours vs 8h/day Mon–Fri since 1 January. Vacation and illness count as 8h; flex, no work, and other count as 0h."
                    >
                      Flex YTD
                    </th>
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={`${u.organisationId}-${u._id}`} className="border-t">
                      <td className="p-2">
                        {u.firstname} {u.lastname}
                      </td>
                      {showOrgColumn && organisations.length > 1 && (
                        <td className="p-2 text-gray-600">{u.organisationName}</td>
                      )}
                      <td className="p-2">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="p-2 text-right">{u.completionPercentage}%</td>
                      <td className="p-2">
                        <MissingDaysCell
                          count={u.missingWorkdays}
                          dates={u.missingDates}
                        />
                      </td>
                      <td className="p-2 text-right">
                        {u.totalHours?.toFixed?.(1) ?? u.totalHours ?? 0}
                      </td>
                      <td className={`p-2 text-right ${getFlexHoursClass(u.flexHours)}`}>
                        {formatFlexHours(u.flexHours)}
                      </td>
                      <td className="p-2">
                        <Link
                          to={buildReportLink(u._id, reportMonth)}
                          className="text-blue-600 hover:underline text-xs whitespace-nowrap"
                        >
                          View reports
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No active members found.</p>
          )}
        </>
      )}
    </Card>
  );
}

function buildReportLink(userId, month) {
  return `/report?userId=${userId}&month=${month}&view=reports`;
}

function StatChip({ label, value, highlight, warn }) {
  const borderClass = highlight
    ? "border-green-200"
    : warn
      ? "border-amber-200"
      : "border-gray-200";

  return (
    <div className={`bg-gray-50 rounded-lg px-4 py-2 border ${borderClass}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTimeReportingStatusClass(status)}`}
    >
      {getTimeReportingStatusLabel(status)}
    </span>
  );
}

function MissingDaysCell({ count, dates }) {
  const preview = formatMissingDatesPreview(dates, 10);

  if (!count) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <div>
      <div className="text-sm font-medium text-gray-900">{count}</div>
      {preview && <div className="text-xs text-gray-400 mt-0.5">{preview}</div>}
    </div>
  );
}
